import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

/**
 * GithubProjectFlowSummary Component
 * Displays GitHub Project (v2) board workflow status and movement metrics
 * Shows status snapshot, monthly movement, staleness, and oldest active items
 */
const GithubProjectFlowSummary = ({ orgName = 'Capital-Land-Management-Services', projectNumber = 28 }) => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projectData, setProjectData] = useState(null);

  //const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color')?.trim() || '#F63040';
  const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color')?.trim() || '#57565B';

  // Fetch project flow data with cache-first strategy and timeout
  const fetchProjectData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    // Create AbortController with 60-second timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);
    
    try {
      // Try cache first
      try {
        const cacheRes = await apiFetch(`/api/Github/project-flow/cache?orgName=${orgName}&projectNumber=${projectNumber}`, { signal: controller.signal });
        if (cacheRes.ok && cacheRes.status !== 204) {
          const cacheData = await cacheRes.json().catch(() => null);
          if (cacheData && cacheData.data) {
            setProjectData(cacheData.data);
            setLoading(false);
            clearTimeout(timeoutId);
            return;
          }
        }
      } catch (catchError) {
          Sentry.captureException(catchError);
        // Silently fail and try live endpoint
      }

      // Fallback to live endpoint
      const response = await apiFetch(`/api/Github/project-flow?orgName=${orgName}&projectNumber=${projectNumber}`, { signal: controller.signal });
      if (!response.ok) {
        if (response.status === 404) {
          setError('Project not found. Check organization name and project number.');
        } else if (response.status === 401) {
          setError('Unauthorized. Check GitHub token configuration and required scopes.');
        } else {
          setError(`Failed to load project data: ${response.status}`);
        }
        setProjectData(null);
        setLoading(false);
        clearTimeout(timeoutId);
        return;
      }
      const result = await response.json().catch(() => null);
      if (result) setProjectData(result.data);
    } catch (e) {
        Sentry.captureException(e);
      if (e.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(e.message || 'Error loading project data');
      }
      setProjectData(null);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [apiFetch, orgName, projectNumber]);

  useEffect(() => { fetchProjectData(); }, [fetchProjectData]);

  // Status colors (GitHub-inspired colors)
  const statusColors = {
    'Backlog': '#6e7681',
    'Problematic': '#d73a4a',
    'Tech Debt': '#bf8700',
    'In Progress': '#1f6feb',
    'Dev QA': '#8957e5',
    'Departmental QA (Staging)': '#fb8500',
    'QA Passed': '#0969da',
    'Deployed to Prod': '#1a7f37'
  };

  const getStatusColor = useCallback((status) => {
    return statusColors[status] || secondaryColor;
  }, [secondaryColor]);

  // Calculate workflow health score (0-100)
  // Per project guidelines: "Large planned backlogs are expected; only stale bugs penalise."
  // Score focuses on active-work flow health, not backlog size.
  const healthScore = useMemo(() => {
    if (!projectData) return null;

    let score = 100;
    const { staleness, movement, statusSnapshot } = projectData;

    // Calculate total items and active work items (exclude Backlog and Tech Debt — those are planned roadmap)
    const totalItems = (statusSnapshot || []).reduce((sum, s) => sum + (s.count || 0), 0);
    if (totalItems === 0) return null;

    const activeWorkItems = (statusSnapshot || [])
      .filter(s => {
        const status = (s.status || '').toLowerCase();
        return status !== 'backlog' && status !== 'tech debt';
      })
      .reduce((sum, s) => sum + (s.count || 0), 0);
    // Use active items as denominator for staleness (min 1 to avoid division by zero)
    const activeDenom = Math.max(activeWorkItems, 1);

    // Staleness penalties — only for active-work statuses
    if (staleness) {
      const inProgressStale = staleness.inProgressOver14Days || 0;
      const devQaStale = staleness.devQaOver14Days || 0;
      const problematicStale = staleness.problematicOver7Days || 0;
      // Tech Debt staleness is informational only — NOT penalised per project guidelines

      // In Progress stale: penalty relative to active work items
      if (inProgressStale > 0) {
        score -= Math.min(25, (inProgressStale / activeDenom) * 100);
      }

      // Dev QA stale: testing bottleneck
      if (devQaStale > 0) {
        score -= Math.min(20, (devQaStale / activeDenom) * 80);
      }

      // Problematic items: explicitly flagged as blocked — critical
      if (problematicStale > 0) {
        score -= Math.min(30, (problematicStale / activeDenom) * 150);
      }
    }

    // Movement bonuses (reward throughput)
    if (movement) {
      const deployed = movement.movedToDeployed || 0;
      const created = movement.created || 0;
      const closed = movement.closed || 0;

      // Positive: items being deployed or closed
      if (deployed > 0 || closed > 0) {
        const completionRate = (deployed + closed) / Math.max(activeDenom, 1);
        score += Math.min(15, completionRate * 30);
      }

      // Net growth penalty only if active work is growing significantly faster than completion
      if (created > 0 && activeWorkItems > 0) {
        const netGrowth = created - deployed - closed;
        if (netGrowth > 0) {
          score -= Math.min(10, (netGrowth / activeDenom) * 50);
        }
      }
    }

    return Math.max(0, Math.min(100, Math.round(score)));
  }, [projectData]);

  const getScoreInterpretation = (score) => {
    if (score === null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 85) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 70) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 55) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 40) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  // Prepare data for status snapshot chart
  const statusChartData = useMemo(() => {
    if (!projectData?.statusSnapshot) return [];
    return projectData.statusSnapshot.map(item => ({
      name: item.status,
      count: item.count,
      fill: getStatusColor(item.status)
    }));
  }, [projectData, getStatusColor]);

  // Prepare staleness summary
  const stalenessData = useMemo(() => {
    if (!projectData?.staleness) return [];
    const staleness = projectData.staleness;
    const items = [];

    if (staleness.inProgressOver14Days > 0) {
      items.push({ label: 'In Progress >14 days', count: staleness.inProgressOver14Days, severity: 'high' });
    }
    if (staleness.devQaOver14Days > 0) {
      items.push({ label: 'Dev QA >14 days', count: staleness.devQaOver14Days, severity: 'high' });
    }
    if (staleness.problematicOver7Days > 0) {
      items.push({ label: 'Problematic >7 days', count: staleness.problematicOver7Days, severity: 'critical' });
    }
    if (staleness.techDebtOver30Days > 0) {
      items.push({ label: 'Tech Debt >30 days (info only)', count: staleness.techDebtOver30Days, severity: 'info' });
    }

    return items;
  }, [projectData]);

  // Format number with space separator
  const formatNumber = (n) => (Number(n) || 0).toLocaleString('en-US').replace(/,/g, ' ');

  if (loading) {
    return (
      <div className="dashboard-card">
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>GitHub Project Workflow</h2>
        <HexagonLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-card">
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>GitHub Project Workflow</h2>
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!projectData) return null;

  const scoreInterp = getScoreInterpretation(healthScore);
  const totalItems = (projectData.statusSnapshot || []).reduce((sum, s) => sum + (s.count || 0), 0);
  const staleItems = (projectData.staleness?.inProgressOver14Days || 0) + 
                     (projectData.staleness?.devQaOver14Days || 0) + 
                     (projectData.staleness?.problematicOver7Days || 0);

  const llmProxyText = [
    `GitHub Project Workflow Health Score: ${healthScore}/100`,
    `Project: ${orgName} #${projectNumber}`,
    `Total Items: ${totalItems}`,
    `Created Last Month: ${projectData.movement?.created || 0}`,
    `Deployed Last Month: ${projectData.movement?.movedToDeployed || 0}`,
    `Stale Items: ${staleItems}`,
    `Problematic Items: ${projectData.staleness?.problematicOver7Days || 0}`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper github-project-flow-summary">
      <div data-llm-proxy style={{ display: 'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop: 0, marginBottom: 12 }}>GitHub Project Workflow</h2>

      {/* Executive Summary Banner */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
              {healthScore !== null ? healthScore : '—'}
              <span className="exec-banner-suffix">/ 100</span>
            </div>
            <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
            <div className="exec-banner-sublabel">Project Workflow Health</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text">
              <strong>What this means:</strong> Project has <strong>{totalItems} active items</strong> across workflow stages.
              {projectData.movement?.movedToDeployed > 0 && <span style={{ color: '#2e7d32' }}> {projectData.movement.movedToDeployed} items deployed last month.</span>}
              {staleItems > 0 && <span style={{ color: '#d73a4a', fontWeight: 600 }}> {staleItems} stale items require attention - work may be blocked.</span>}
              {projectData.staleness?.problematicOver7Days > 0 && <span style={{ color: '#cf222e', fontWeight: 700 }}> {projectData.staleness.problematicOver7Days} items marked problematic need immediate resolution.</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card">
          <div className="metric-label">Total Items</div>
          <div className="metric-value">{formatNumber(totalItems)}</div>
          <div className="metric-subtitle">Across all stages</div>
        </div>
        <div className="dashboard-card metric-card">
          <div className="metric-label">Deployed (Month)</div>
          <div className="metric-value" style={{ color: '#1a7f37' }}>{formatNumber(projectData.movement?.movedToDeployed || 0)}</div>
          <div className="metric-subtitle">Completed & released</div>
        </div>
        <div className="dashboard-card metric-card">
          <div className="metric-label">In Progress</div>
          <div className="metric-value" style={{ color: '#1f6feb' }}>{formatNumber(projectData.movement?.movedToInProgress || 0)}</div>
          <div className="metric-subtitle">Started last month</div>
        </div>
        <div className="dashboard-card metric-card">
          <div className="metric-label">Stale Items</div>
          <div className="metric-value" style={{ color: staleItems > 0 ? '#d73a4a' : '#2e7d32' }}>{formatNumber(staleItems)}</div>
          <div className="metric-subtitle">Potentially blocked</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="dashboard-grid compact">
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Current Status Distribution</h4>
          <div className="chart-subtitle">Items by workflow stage</div>
          <ResponsiveContainer width={isPrintMode ? 400 : "100%"} height={280}>
            <BarChart data={statusChartData} margin={{ top: 10, right: 10, bottom: isPrintMode ? 80 : 60, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              {/* Print renders this chart at a fixed 400px width; with up to 6
                  workflow-stage categories (some long, e.g. "Departmental QA
                  (Stale)") horizontal labels overlap each other illegibly.
                  Angle the labels in print so they read diagonally instead. */}
              <XAxis
                dataKey="name"
                angle={isPrintMode ? -35 : 0}
                textAnchor={isPrintMode ? "end" : "middle"}
                interval={0}
                height={isPrintMode ? 80 : 60}
                tick={{ fontSize: isPrintMode ? 9 : 10 }}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={!isPrintMode}>
                {statusChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Staleness Alerts */}
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Staleness Alerts</h4>
          <div className="chart-subtitle">Items exceeding expected duration</div>
          {stalenessData.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#2e7d32', fontSize: 13 }}>
              ✓ No stale items detected - workflow is healthy
            </div>
          ) : (
            <div style={{ padding: '12px 8px' }}>
              {stalenessData.map((item, idx) => (
                <div 
                  key={idx} 
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '10px 12px', 
                    marginBottom: 8,
                    borderRadius: 4,
                    background: item.severity === 'critical' ? '#fef2f2' : item.severity === 'high' ? '#fff7ed' : item.severity === 'info' ? '#eff6ff' : '#fefce8',
                    borderLeft: `3px solid ${item.severity === 'critical' ? '#dc2626' : item.severity === 'high' ? '#ea580c' : item.severity === 'info' ? '#3b82f6' : '#ca8a04'}`
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 500 }}>{item.label}</span>
                  <span style={{ 
                    fontSize: 16, 
                    fontWeight: 700, 
                    color: item.severity === 'critical' ? '#dc2626' : item.severity === 'high' ? '#ea580c' : item.severity === 'info' ? '#3b82f6' : '#ca8a04'
                  }}>
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top 10 Oldest Active Items */}
      {projectData.oldestActiveItems && projectData.oldestActiveItems.length > 0 && (
        <div className="dashboard-card">
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Oldest Active Items</h4>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
            Items with longest time in current status - may need attention
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ textAlign: 'left', padding: '6px', fontSize: 11, maxWidth: 300 }}>Title</th>
                  <th style={{ padding: '6px', fontSize: 11 }}>Status</th>
                  <th style={{ padding: '6px', fontSize: 11 }}>Days in Status</th>
                  <th style={{ padding: '6px', fontSize: 11 }}>Assignee</th>
                </tr>
              </thead>
              <tbody>
                {projectData.oldestActiveItems.slice(0, 10).map((item, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ textAlign: 'left', padding: '6px', fontSize: 11, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.url ? (
                        <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#0969da', textDecoration: 'none' }}>
                          {item.title}
                        </a>
                      ) : item.title}
                    </td>
                    <td style={{ padding: '6px', fontSize: 11 }}>
                      <span style={{ 
                        padding: '2px 8px', 
                        borderRadius: 12, 
                        background: getStatusColor(item.status) + '20',
                        color: getStatusColor(item.status),
                        fontSize: 10,
                        fontWeight: 600
                      }}>
                        {item.status}
                      </span>
                    </td>
                    <td style={{ padding: '6px', fontSize: 11, fontWeight: 600 }}>
                      {item.daysInStatus !== null ? item.daysInStatus : '—'}
                    </td>
                    <td style={{ padding: '6px', fontSize: 11 }}>{item.assignee || 'Unassigned'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Explanation Section */}
      <div className="explanation-box">
        <strong>Understanding GitHub Project Workflow Metrics:</strong>
        <ul>
          <li><strong>Workflow Health Score:</strong> Measures active-work flow health based on staleness of in-progress items and deployment throughput. Backlog and Tech Debt items are planned roadmap items and are <strong>not penalised</strong> — large planned backlogs are expected.</li>
          <li><strong>Status Distribution:</strong> Shows where work sits in your pipeline. Concentration in active-work stages (In Progress, Dev QA) may indicate bottlenecks.</li>
          <li><strong>Deployment Rate:</strong> Items moving to "Deployed to Prod" status during the reporting month. Higher deployment frequency indicates productive development.</li>
          <li><strong>Staleness Alerts:</strong> Items exceeding expected duration in <em>active-work</em> statuses only. Stale active items often indicate blockers or resource constraints.</li>
          <li><strong>In Progress &gt;14 days:</strong> Active development work taking longer than expected — may need pairing, unblocking, or scope clarification.</li>
          <li><strong>Dev QA &gt;14 days:</strong> Testing phase dragging — could indicate test environment issues or resource bottlenecks.</li>
          <li><strong>Problematic Items:</strong> Explicitly flagged as blocked — require immediate management attention to unblock.</li>
          <li><strong>Tech Debt (info only):</strong> Shown for awareness but does not affect the health score. Tech Debt items are planned improvements that naturally sit longer.</li>
          <li><strong>Oldest Active Items:</strong> Longest-running items in active-work statuses (excludes Backlog and Tech Debt). Review for scope creep or blocked dependencies.</li>
        </ul>
      </div>
    </div>
  );
};

export default GithubProjectFlowSummary;
